<template>
  <el-dialog title="移库明细" :visible.sync="dialogVisible" :close-on-click-modal="false"
    append-to-body width="1280px" class="batchMovePreviewDialog" @closed="reset">
    <div class="previewContent" v-loading="loading">
      <!-- 批量移库明细完全按照预览接口返回的数据展示，不从核查列表拼装库存。 -->
      <el-table ref="moveTable" :data="tableData" border empty-text="暂无可移库明细"
        @selection-change="handleSelectionChange">
        <!-- 使用 Element UI 原生选择列，由表格统一处理单选、全选和禁用状态。 -->
        <el-table-column type="selection" width="64" align="center" :selectable="hasMovableStock"
          class-name="moveCheckboxColumn" label-class-name="moveCheckboxColumn" />

        <el-table-column type="index" label="NO" width="55" align="center" />

        <el-table-column label="订单编号" min-width="150" align="center">
          <template slot-scope="scope">
            <div class="orderInfo">
              <p>{{ scope.row.order_sn }}</p>
              <p>{{ scope.row.realname }}</p>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="番号" prop="sorting" width="60" align="center" />

        <el-table-column label="图片" width="82" align="center">
          <template slot-scope="scope">
            <el-popover placement="right" width="300" trigger="hover">
              <img :src="scope.row.pic" alt="" class="previewLargeImage" />
              <img slot="reference" :src="scope.row.pic" alt="" class="productImage" />
            </el-popover>
          </template>
        </el-table-column>

        <el-table-column label="业务编辑属性" min-width="150">
          <template slot-scope="scope">
            <p v-for="(detail, index) in scope.row.detail_y" :key="index" class="detailItem">
              {{ detail.key }}: {{ detail.value }}
            </p>
          </template>
        </el-table-column>

        <el-table-column label="数量" width="112">
          <template slot-scope="scope">
            <p>可入库：{{ scope.row.possible_num }}</p>
            <p>已到货：{{ scope.row.storage_num }}</p>
            <p>在库：{{ scope.row.stock_num }}</p>
          </template>
        </el-table-column>

        <el-table-column label="库位" min-width="290">
          <template slot-scope="scope">
            <div v-for="stock in scope.row.wms_stock" :key="stock.wms_stock_id" class="stockValueRow gridNumber">
              <!-- 区域用途编码拼在完整库位前，具体库位段继续单独高亮。 -->
              <span v-if="stock.area_type_code">{{ stock.area_type_code }}-</span>
              <span>{{ stock.grid_number.split("】-")[0] }}】-</span>
              <span class="gridNumberHighlight">{{ stock.grid_number.split("】-")[1] }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="可移库数" width="90" align="center">
          <template slot-scope="scope">
            <div v-for="stock in scope.row.wms_stock" :key="stock.wms_stock_id" class="stockValueRow movableNumber">
              {{ stock.can_move_num }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="需要移库数量" width="130" align="center">
          <template slot-scope="scope">
            <div v-for="stock in scope.row.wms_stock" :key="stock.wms_stock_id" class="stockValueRow">
              <el-input v-model="stock.move_num" type="number" size="mini" :min="0"
                :max="Number(stock.can_move_num)" :disabled="!scope.row.checked"
                @input="limitMoveNum(stock)" />
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">{{ $t("message.cancel") }}</el-button>
      <el-button type="primary" :disabled="loading || tableData.length === 0" @click="confirmMove">
        确认移库
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  data() {
    return {
      dialogVisible: false,
      loading: false,
      tableData: [],
    }
  },
  methods: {
    /**
     * 打开批量移库明细，并按当前勾选的采购记录请求真实库存。
     * @param {Array<number>} orderPurchaseIds 采购记录 ID 集合
     */
    open(orderPurchaseIds) {
      this.dialogVisible = true
      this.loading = true
      this.tableData = []
      this.$api.followApi.batchMovePreview({
        order_purchase_id_set: orderPurchaseIds,
      }).then((res) => {
        this.tableData = (res.data || []).map((item) => {
          const wmsStock = (item.wms_stock || []).map((stock) => ({
            ...stock,
            move_num: Number(stock.can_move_num) > 0 ? Number(stock.can_move_num) : 0,
          }))
          return {
            ...item,
            checked: false,
            wms_stock: wmsStock,
          }
        })
        // 接口返回后默认选中所有可移库行，并同步启用数量输入框。
        this.$nextTick(() => {
          this.tableData.forEach((item) => {
            if (this.hasMovableStock(item)) this.$refs.moveTable.toggleRowSelection(item, true)
          })
        })
      }).finally(() => {
        this.loading = false
      })
    },
    // 仅允许勾选存在可移库库存的商品。
    hasMovableStock(row) {
      return (row.wms_stock || []).some(stock => Number(stock.can_move_num) > 0)
    },
    // 原生选择列变化后同步每行状态，供移库数量输入框和提交数据使用。
    handleSelectionChange(selection) {
      this.tableData.forEach((item) => {
        this.$set(item, "checked", selection.indexOf(item) !== -1)
      })
    },
    // 移库数量必须为整数，且不能超过接口返回的可移库数量。
    limitMoveNum(stock) {
      const max = Number(stock.can_move_num) || 0
      const value = parseInt(stock.move_num, 10)
      if (Number.isNaN(value) || value < 0) {
        stock.move_num = 0
        return
      }
      stock.move_num = Math.min(value, max)
    },
    /**
     * 只整理弹窗中已勾选番的库存主键与数量，并交给父页面继续选择目标库位。
     */
    confirmMove() {
      const list = []
      const orderPurchaseIds = []
      this.tableData.forEach((item) => {
        if (!item.checked) return
        let hasMoveStock = false
        item.wms_stock.forEach((stock) => {
          const num = Number(stock.move_num)
          if (num > 0) {
            list.push({
              wms_stock_id: String(stock.wms_stock_id),
              num: String(num),
            })
            hasMoveStock = true
          }
        })
        if (hasMoveStock) orderPurchaseIds.push(item.order_purchase_id)
      })
      if (list.length === 0) return this.$message.error("请填写需要移库的数量")
      this.$emit("confirm", list, orderPurchaseIds)
    },
    // 仅在移库接口执行成功后由父页面关闭，取消选库或提交失败时保留当前明细。
    close() {
      this.dialogVisible = false
    },
    // 弹框关闭后清理上一次预览，避免再次打开时短暂显示旧数据。
    reset() {
      this.tableData = []
      this.loading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.previewContent {
  min-height: 180px;
}

.orderInfo,
.detailItem,
.stockRow p {
  margin: 0;
}

.productImage {
  width: 52px;
  height: 52px;
  object-fit: cover;
  border: 1px solid #dcdfe6;
}

.previewLargeImage {
  display: block;
  width: 300px;
  height: 300px;
  object-fit: contain;
}

.detailItem {
  line-height: 22px;
}

.stockValueRow {
  display: flex;
  align-items: center;
  min-height: 34px;

  & + .stockValueRow {
    border-top: 1px solid #ebeef5;
  }
}

.gridNumber {
  color: #606266;
}

.gridNumberHighlight {
  margin-left: 4px;
  color: #f56c6c;
  font-weight: 600;
}

.movableNumber {
  color: #f56c6c;
  justify-content: center;
}

/deep/ .el-dialog {
  max-width: 94vw;
}

/deep/ .el-dialog__body {
  padding: 12px 24px;
}

/deep/ .el-table th {
  background: #e6e6e6;
  color: #303133;
}

// 为放大后的复选框预留完整空间，避免被 Element 表格单元格的 overflow 裁切。
.batchMovePreviewDialog /deep/ th.moveCheckboxColumn,
.batchMovePreviewDialog /deep/ td.moveCheckboxColumn,
.batchMovePreviewDialog /deep/ .moveCheckboxColumn .cell {
  overflow: visible !important;
}

.batchMovePreviewDialog /deep/ .moveCheckboxColumn .cell {
  padding-right: 0;
  padding-left: 0;
}

.batchMovePreviewDialog /deep/ .el-table .el-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 0;
  vertical-align: middle;
}

.batchMovePreviewDialog /deep/ .el-table .el-checkbox__input {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.batchMovePreviewDialog /deep/ .el-table .el-checkbox__inner {
  width: 14px;
  height: 14px;
  border-radius: 2px;
  transform: scale(2) !important;
  transform-origin: center;
}

.batchMovePreviewDialog /deep/ .el-table .el-checkbox__input.is-checked .el-checkbox__inner,
.batchMovePreviewDialog /deep/ .el-table .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  border-color: #b9272d;
  background-color: #b9272d;
}

/deep/ .stockValueRow .el-input {
  width: 100%;
}
</style>
